import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { withLDConsumer } from 'launchdarkly-react-client-sdk'
import { Helmet } from 'react-helmet'
import { Link, useHistory, useLocation } from 'react-router-dom'
import queryString from 'query-string'
import QRCode from 'react-qr-code'

import { AbstractForm } from '@/components/forms/formik/AbstractForm'
import { Banner } from '@/components/banners/Banner'
import { Button } from '@/components/buttons/Button'

import { AuthContainer } from '@/views/Auth/AuthContainer'
import { enrollMFA, validateMFA, resendMFAEmail } from '@/reducers/auth/mfa.actions'

const generateQRCode = (uri) => {
  return (
    <div>
      <QRCode data-testid="mfa-qr" value={uri} size={160} level="M" />
    </div>
  )
}

const MFALoginPageComponent = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const [emailResent, setEmailResent] = useState(false)

  const { next } = queryString.parse(location.search)

  const { pendingToken, mfaType, uri, manualKey, enrolling, codeError, username } = useSelector(
    (state) => state.mfa
  )

  // if user is on the page without a pending token, take them to login page
  if (!pendingToken) {
    history.push('/login')
  }

  const handleMfaTypeSelect = (selectedType) => {
    dispatch(enrollMFA(selectedType, history))
  }

  const handleCodeSubmit = ({ code }, { setSubmitting }) => {
    dispatch(validateMFA(code, mfaType, enrolling, history, setSubmitting, next))
  }

  const handleResendEmail = () => {
    dispatch(resendMFAEmail(history))
    setEmailResent(true)
    // re-enable after 15 seconds
    setTimeout(() => setEmailResent(false), 15000)
  }

  const resendEmailButton = (
    <div className="text-center mt">
      <Button
        link
        onClick={handleResendEmail}
        data-testid="mfa-resend-email"
        disabled={emailResent}
      >
        {emailResent ? `Email resent to ${username}` : 'Resend email'}
      </Button>
    </div>
  )

  const selectMFA = (
    <div>
      <label>Select MFA Type</label>

      <div className="flex-align-center gap mt">
        <Button fullWidth onClick={() => handleMfaTypeSelect('email')} data-testid="mfa-type-email">
          Email
        </Button>

        <Button fullWidth onClick={() => handleMfaTypeSelect('totp')} data-testid="mfa-type-totp">
          Authenticator App
        </Button>
      </div>
    </div>
  )

  const getMFAInstructions = () => {
    let setUpSteps = null
    // if we got a URI, that means we need the user to set up their MFA app
    if (uri) {
      setUpSteps = (
        <div className="flex-column flex-align-center text-center mb">
          <h2>Set up Two-Factor Authentication</h2>
          <p>Scan this QR code or manually enter the key below in your authenticator app.</p>
          <p>Enter the code from your authenticator app to continue.</p>
          {generateQRCode(uri)}
          <div data-testid="mfa-manual-key">{manualKey}</div>
        </div>
      )
    }

    let instructions
    if (mfaType === 'email') {
      instructions = 'Enter verification code sent to email'
    } else if (mfaType === 'totp') {
      instructions = 'Enter verification code from authenticator app'
    }

    return (
      <div>
        {setUpSteps}
        <AbstractForm
          schema={[{ name: 'code', type: 'text', label: instructions, required: true }]}
          onSubmit={handleCodeSubmit}
          buttonProps={{ fullWidth: true }}
          buttonLabel="Submit Code"
        />
        {mfaType === 'email' && resendEmailButton}
        {codeError && (
          <Banner critical>
            <div className="text-center">Invalid code. Please try again.</div>
          </Banner>
        )}
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>Login | Balto Cloud</title>
      </Helmet>
      <AuthContainer>
        <h2 data-testid="mfa-login-page">Multi-Factor Authentication Required</h2>
        {mfaType ? getMFAInstructions() : selectMFA}
        <Link to="/login" className="link">
          Back to login
        </Link>
      </AuthContainer>
    </>
  )
}

export default withLDConsumer()(MFALoginPageComponent)
